import { useState, useEffect } from "react";
import { X, Loader2, CheckCircle2, CalendarDays } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/components/booking/bookingMeta";

function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function SurveyModal({ open, propertyId, propertyName, room, onClose, onSuccess }) {
  const [surveyDate, setSurveyDate] = useState("");
  const [surveyTime, setSurveyTime] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setSurveyDate(""); setSurveyTime(""); setNotes(""); setError(""); setResult(null); setSubmitting(false); }
  }, [open, propertyId, room?.id]);

  if (!open) return null;

  const close = () => { onClose?.(); };

  const submit = async () => {
    setError("");
    if (!surveyDate) { setError("Pilih tanggal survei."); return; }
    if (surveyDate < todayISO()) { setError("Tanggal survei tidak boleh di masa lalu."); return; }
    if (!surveyTime) { setError("Pilih jam survei."); return; }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/surveys", {
        property_id: propertyId,
        room_id: room?.id || null,
        survey_date: surveyDate,
        survey_time: surveyTime,
        notes: notes.trim() || null,
      });
      setResult(data);
      onSuccess?.(data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="survey-modal" onClick={close}>
      <div className="bg-card rounded-2xl border border-border w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 p-5 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <CalendarDays size={18} className="text-primary shrink-0" />
            <div className="min-w-0">
              <h3 className="font-heading text-lg font-semibold">Jadwalkan Survei</h3>
              {propertyName && <p className="text-sm text-muted-foreground truncate">{propertyName}</p>}
            </div>
          </div>
          <button onClick={close} data-testid="survey-modal-close"
            className="grid place-items-center w-9 h-9 rounded-full border border-border text-muted-foreground hover:bg-secondary/60 transition-colors shrink-0">
            <X size={16} />
          </button>
        </div>

        {result ? (
          <div className="p-6 text-center" data-testid="survey-success">
            <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3" />
            <p className="font-semibold">Jadwal survei diajukan!</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {formatDate(result.survey_date)} · {result.survey_time} — status <span className="font-medium text-foreground">Menunggu Konfirmasi</span>.
              Pemilik akan mengonfirmasi jadwal Anda.
            </p>
            <button onClick={close} data-testid="survey-done-button"
              className="mt-5 w-full py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform">
              Selesai
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {room && (
              <div className="rounded-xl bg-secondary/40 px-4 py-2.5" data-testid="survey-room-info">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Kamar</p>
                <p className="mt-0.5 text-sm font-semibold">{room.room_number}{room.name ? ` · ${room.name}` : ""}</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">Atur jadwal kunjungan untuk melihat {room ? "kamar ini" : "properti"} sebelum memutuskan. Tidak perlu booking terlebih dahulu.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Tanggal</label>
                <input type="date" value={surveyDate} min={todayISO()} onChange={(e) => setSurveyDate(e.target.value)}
                  data-testid="survey-date-input"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Jam</label>
                <input type="time" value={surveyTime} onChange={(e) => setSurveyTime(e.target.value)}
                  disabled={!surveyDate} data-testid="survey-time-input"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Catatan (opsional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="survey-notes"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Mis. ingin melihat kamar lantai atas" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="survey-error">{error}</p>}
            <button onClick={submit} disabled={submitting} data-testid="survey-submit-button"
              className="w-full py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <CalendarDays size={16} />} Ajukan Survei
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
